import type { BrandPalette } from '../types';
import { FONT_FAMILIES } from '../types';

export type KnobKind =
  | 'color'
  | 'radius'
  | 'shadow'
  | 'fontFamily'
  | 'fontWeight'
  | 'fontSize'
  | 'letterSpacing'
  | 'opacity'
  | 'number'
  /** Free-form string value — used for variant-style enum switches
   *  (e.g. `BottomActionStyle.style ∈ {'disc','pill'}`). */
  | 'text'
  /** Image URL written into a CSS `background-image`. The operator
   *  pastes a raw URL (e.g. `https://cdn.example.com/foo.jpg`); the
   *  formatter auto-wraps it with `url("…")` so the var is a valid
   *  CSS image value. Pre-wrapped `url(...)` / `linear-gradient(...)` /
   *  `none` values are passed through untouched. */
  | 'imageUrl';

export type KnobDefault =
  | { kind: 'literal'; value: string | number }
  | { kind: 'role'; role: keyof BrandPalette }
  /** Default = current resolved value of another component's knob. Used
   *  for cascade defaults: page-specific backgrounds inherit from the
   *  global PageBackground, which itself falls back to palette via
   *  `role`. Matches the CSS fallback chain so placeholder text in the
   *  editor always shows the same colour the user actually sees on the
   *  page. */
  | { kind: 'componentKnob'; component: string; knob: string };

export interface KnobDef {
  kind: KnobKind;
  label: string;
  default: KnobDefault;
  /** When set, the knob renders as a select-dropdown of these values
   *  instead of a free-form text input. Use for font-family pickers and
   *  any other small enum where we want to constrain the operator to a
   *  pre-approved list (e.g. `FONT_FAMILIES`). */
  options?: readonly string[];
}

export type ComponentTemplate = 'classic' | 'modern';

/**
 * Scenes where a registry component is actually rendered. Mirrors the
 * route the iframe is currently on. `useIframeScene` reads the iframe's
 * pathname and the sidebar filters COMPONENTS down to entries whose
 * `scenes` includes it (or that have no `scenes` field at all — those
 * are cross-cutting, e.g. global PageBackground).
 *
 *   pack         — Pack page (`/`)
 *   just-pulled  — Just Pulled list (`/just-pulled`)
 *   my-packs     — My Packs page (`/my-packs`)
 *   reveal       — Reveal flow (only visible after RIP — operator can't
 *                  navigate to it from the constructor; kept so reveal-
 *                  only components stay in the schema)
 */
export type Scene = 'pack' | 'just-pulled' | 'my-packs' | 'reveal';

export interface ComponentSchema {
  /** Technical identifier — drives `--co-<name>-<knob>` CSS variable names.
   *  Must never be renamed without coordinated changes to existing themes. */
  name: string;
  /** Optional human-friendly label shown in the Design Constructor UI.
   *  Falls back to `name` when omitted. Pure presentation — does not affect
   *  CSS vars or storage keys. */
  displayName?: string;
  group: 'Buttons' | 'Badges' | 'Panels' | 'Misc' | 'Layout' | 'Modals' | 'Reveal';
  /** Scenes in which the component is actually visible. `undefined` means
   *  "shown in every scene" — the editor renders it under every section.
   *  Pure presentation, never affects CSS vars. */
  scenes?: Scene[];
  /** Allowed values for a variant picker. First entry = default selection.
   *  Used by knobs of kind `text` that act as enum-style switches (e.g.
   *  `BottomActionShell.gradientDirection ∈ {'to bottom','to right'}`). */
  variants?: string[];
  /** Which knob the variant picker writes its selection into. Defaults to
   *  `'style'` for backwards compatibility. */
  variantKnob?: string;
  /** Sidebar label rendered above the variant radios. Defaults to
   *  `'Variant'`. */
  variantLabel?: string;
  /** If set, the component is only rendered by this template — editor
   *  filters it out when the user picks the other template. `undefined`
   *  means the component is shared by both templates. */
  template?: ComponentTemplate;
  knobs: Record<string, KnobDef>;
}

// Common shortcut helpers to reduce repetition.
const role = (r: keyof BrandPalette): KnobDefault => ({ kind: 'role', role: r });
const lit = (v: string | number): KnobDefault => ({ kind: 'literal', value: v });
const inherit = (component: string, knob: string): KnobDefault => ({
  kind: 'componentKnob',
  component,
  knob,
});

export const COMPONENTS: readonly ComponentSchema[] = [
  // --- Buttons -------------------------------------------------------------
  {
    name: 'Button',
    displayName: 'Generic Button (DS)',
    group: 'Buttons',
    // No `scenes` field = cross-cutting. The DS `<Button>` renders on
    // multiple routes (Pack page RIP / Rip-Repackz on JustPulled / Sell
    // actions on MyPacks), so anchoring it to one scene would hide it
    // from the sidebar exactly when the operator clicks it.
    // Per-variant theming. Each of the 8 visual variants owns its own
    // {fill,text,border} triplet. `variantKnob` is intentionally omitted —
    // the picker runs in UI-only mode (see VariantPicker.value/onChange):
    // changing the variant only filters which knobs are visible, it does
    // NOT write a CSS variable. The `variants` list and the knob-key
    // prefixes must stay in lock-step with `Button.tsx` and with the
    // ComponentSection `key.startsWith(currentVariant)` filter.
    variants: [
      'primary',
      'secondary',
      'ghost',
      'icon',
      'glass',
      'glass-frosted',
      'glass-icon',
      'accent-gloss',
      'header-icon',
    ],
    variantLabel: 'Button variant',
    knobs: {
      primaryFill: { kind: 'color', label: 'Fill', default: role('primary') },
      primaryText: { kind: 'color', label: 'Text colour', default: role('textOnBrand') },
      primaryBorder: { kind: 'color', label: 'Border', default: lit('transparent') },
      secondaryFill: { kind: 'color', label: 'Fill', default: role('surfaceBg') },
      secondaryText: { kind: 'color', label: 'Text colour', default: role('textPrimary') },
      secondaryBorder: { kind: 'color', label: 'Border', default: role('surfaceBorder') },
      ghostFill: { kind: 'color', label: 'Fill', default: lit('transparent') },
      ghostText: { kind: 'color', label: 'Text colour', default: role('textPrimary') },
      ghostBorder: { kind: 'color', label: 'Border', default: lit('transparent') },
      iconFill: { kind: 'color', label: 'Fill', default: role('surfaceBg') },
      iconText: { kind: 'color', label: 'Icon colour', default: role('textPrimary') },
      // `icon` renders borderless by default; width defaults to 0 so the
      // border stays invisible until the operator sets iconBorderWidth.
      // (Default changed from role('surfaceBorder') → transparent now that
      // the slot is actually consumed — a surface-coloured ring would have
      // appeared the moment the variant grew a border.)
      iconBorder: { kind: 'color', label: 'Border', default: lit('transparent') },
      glassFill: { kind: 'color', label: 'Fill', default: lit('rgba(255,255,255,0.1)') },
      glassText: { kind: 'color', label: 'Text colour', default: lit('#FFFFFF') },
      glassBorder: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.25)') },
      // glass-frosted — Figma 305:26637 two-layer tint, 27px blur,
      // softer hairline border + drop-shadow. Used by My Packs / Delivery
      // headers' "Home" pill and the "+ Add Delivery" CTA.
      'glass-frostedFill': {
        kind: 'color',
        label: 'Fill',
        default: lit(
          'linear-gradient(180deg, rgba(255,255,255,0.20) 0%, rgba(153,153,153,0.20) 100%), linear-gradient(90deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0.18) 100%)',
        ),
      },
      'glass-frostedText': { kind: 'color', label: 'Text colour', default: lit('#FFFFFF') },
      'glass-frostedBorder': { kind: 'color', label: 'Border', default: lit('rgba(171,174,179,0.24)') },
      'glass-iconFill': { kind: 'color', label: 'Fill', default: lit('rgba(255,255,255,0.1)') },
      'glass-iconText': { kind: 'color', label: 'Icon colour', default: lit('#FFFFFF') },
      'glass-iconBorder': { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.25)') },
      'accent-glossFill': { kind: 'color', label: 'Fill', default: role('accent') },
      'accent-glossText': { kind: 'color', label: 'Text colour', default: role('textOnBrand') },
      'accent-glossBorder': { kind: 'color', label: 'Border', default: lit('transparent') },
      'header-iconFill': { kind: 'color', label: 'Fill', default: lit('#FFFFFF') },
      'header-iconText': { kind: 'color', label: 'Icon colour', default: role('textPrimary') },
      'header-iconBorder': { kind: 'color', label: 'Border', default: lit('transparent') },

      // ── Border width (px) — per variant. Defaults match today: 1px on
      //    bordered variants, 0 on `icon` (renders borderless). ──────────
      primaryBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      secondaryBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      ghostBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      iconBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(0) },
      glassBorderWidth: { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      'glass-frostedBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      'glass-iconBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      'accent-glossBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1) },
      'header-iconBorderWidth': { kind: 'radius', label: 'Border width (px)', default: lit(1) },

      // ── Shadow (box-shadow) — per variant. One field covers both inner
      //    (`inset …`) and drop shadows. Defaults = exact current literals
      //    so an un-overridden button is pixel-identical. ────────────────
      primaryShadow: { kind: 'shadow', label: 'Shadow', default: lit('inset 0 -6px 44px rgba(255,255,255,0.59), inset 0 -6px 14px rgba(255,255,255,0.19)') },
      secondaryShadow: { kind: 'shadow', label: 'Shadow', default: lit('0 1px 2px #1919191A') },
      ghostShadow: { kind: 'shadow', label: 'Shadow', default: lit('none') },
      iconShadow: { kind: 'shadow', label: 'Shadow', default: lit('0 0 1px rgba(0,0,0,0.25)') },
      glassShadow: { kind: 'shadow', label: 'Shadow', default: lit('none') },
      'glass-frostedShadow': { kind: 'shadow', label: 'Shadow', default: lit('0 64px 64px 0 rgba(0,0,0,0.05)') },
      // 27px backdrop-blur is the visual signature of the frosted-glass
      // chip from Figma 305:26637 — strong enough that the layer behind
      // it is unmistakably softened but still legible. Operators can
      // dial it down (or to 0 for an opaque pill) when the surface
      // behind the button isn't busy enough to warrant the blur cost.
      'glass-frostedBlur': { kind: 'radius', label: 'Backdrop blur (px)', default: lit(27) },
      'glass-iconShadow': { kind: 'shadow', label: 'Shadow', default: lit('none') },
      // accent-gloss keeps its built-in bright inner gloss (a fixed overlay
      // element); this knob adds an OPTIONAL box-shadow on the button, off
      // (none) by default.
      'accent-glossShadow': { kind: 'shadow', label: 'Shadow', default: lit('none') },
      'header-iconShadow': { kind: 'shadow', label: 'Shadow', default: lit('0 9px 30px 0 rgba(7,10,18,0.08)') },

      // ── Font family — text-bearing variants only. Default Inter; runtime
      //    falls back to `inherit` (= today's behaviour) when unset. ──────
      primaryFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      secondaryFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      ghostFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      glassFontFamily: { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      'glass-frostedFontFamily': { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },
      'accent-glossFontFamily': { kind: 'fontFamily', label: 'Font family', default: lit('Inter'), options: FONT_FAMILIES },

      // ── Border radius (px) — per variant. Defaults reproduce today's
      //    rounded-* (pill ≈ 9999, icon = --radius-button = 24, gloss = 28). ─
      primaryBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      secondaryBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      ghostBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      iconBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(24) },
      glassBorderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      'glass-frostedBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      'glass-iconBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
      'accent-glossBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
      'header-iconBorderRadius': { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },

      // ── Font size (px) — text variants only. Default 15 (body); runtime
      //    falls back to the size-appropriate token when left unset. ───────
      primaryFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      secondaryFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      ghostFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      glassFontSize: { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      'glass-frostedFontSize': { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },
      'accent-glossFontSize': { kind: 'fontSize', label: 'Font size (px)', default: lit(15) },

      // ── Font weight — text variants only. Default 600 (matches the old
      //    type-*-semi utilities). ────────────────────────────────────────
      primaryFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      secondaryFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      ghostFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      glassFontWeight: { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      'glass-frostedFontWeight': { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
      'accent-glossFontWeight': { kind: 'fontWeight', label: 'Font weight', default: lit(600) },
    },
  },
  {
    name: 'TabButton',
    displayName: 'Tab button',
    group: 'Buttons',
    scenes: ['pack'],
    knobs: {
      activeFill: { kind: 'color', label: 'Active background', default: lit('#FFFFFF') },
      text: { kind: 'color', label: 'Active text colour', default: role('textPrimary') },
      idleFill: { kind: 'color', label: 'Idle background', default: lit('transparent') },
      idleText: { kind: 'color', label: 'Idle text colour', default: role('textPrimary') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(60) },
    },
  },

  // --- Badges --------------------------------------------------------------
  {
    name: 'CountBadge',
    displayName: 'Tier badge — count chip',
    group: 'Badges',
    scenes: ['pack'],
    knobs: {
      activeFill: { kind: 'color', label: 'Active fill', default: role('accent') },
      activeText: { kind: 'color', label: 'Active text', default: role('textOnBrand') },
      idleFill: { kind: 'color', label: 'Idle fill', default: lit('#FFFFFF') },
      idleText: { kind: 'color', label: 'Idle text', default: role('textPrimary') },
    },
  },
  {
    name: 'RarityBadge',
    displayName: 'Pack-logo sticker',
    group: 'Badges',
    scenes: ['pack'],
    knobs: {
      stickerGradientFrom: { kind: 'color', label: 'Sticker — gradient start', default: lit('#E99F1F') },
      stickerGradientTo: { kind: 'color', label: 'Sticker — gradient end', default: lit('#FFFFFF') },
      stickerBorder: { kind: 'color', label: 'Sticker border', default: lit('rgba(255,255,255,0.38)') },
      stickerText: { kind: 'color', label: 'Sticker text', default: role('textOnBrand') },
      borderRadius: { kind: 'radius', label: 'Sticker corner radius (px)', default: lit(10) },
    },
  },
  {
    name: 'ExpectedValueBadge',
    displayName: 'EV badge ($25.00)',
    group: 'Badges',
    scenes: ['pack'],
    template: 'classic',
    knobs: {
      bgTint: { kind: 'color', label: 'Background tint', default: lit('rgba(37,234,30,0.22)') },
      text: { kind: 'color', label: 'Text', default: lit('#25ea1e') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(8) },
    },
  },
  {
    name: 'TierDot',
    displayName: 'Tier badge — coloured dot',
    group: 'Badges',
    scenes: ['pack'],
    knobs: {
      baseColor: { kind: 'color', label: 'Base colour', default: role('accent') },
    },
  },
  {
    name: 'SoldStatusPanel',
    displayName: 'Sold-out status panel',
    group: 'Badges',
    scenes: ['reveal'],
    knobs: {
      accent: { kind: 'color', label: 'Accent (border + text)', default: role('success') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'BuybackPricePill',
    displayName: 'Buyback price pill',
    group: 'Badges',
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: lit('#F2F2F2') },
      text: { kind: 'color', label: 'Text', default: role('textPrimary') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(50) },
    },
  },
  {
    name: 'PotentialHitPricePill',
    displayName: 'Potential-hits price pill',
    group: 'Badges',
    scenes: ['pack'],
    knobs: {
      fill: { kind: 'color', label: 'Background', default: role('surfaceBg') },
      text: { kind: 'color', label: 'Text', default: role('textPrimary') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(9999) },
    },
  },

  // --- Panels --------------------------------------------------------------
  {
    name: 'GlassPanel',
    displayName: 'Glass overlay card',
    group: 'Panels',
    scenes: ['reveal'],
    knobs: {
      tint: { kind: 'color', label: 'Glass tint', default: lit('rgba(29,30,31,0.2)') },
      border: { kind: 'color', label: 'Border', default: lit('rgba(255,255,255,0.07)') },
      radius: { kind: 'radius', label: 'Corner radius (px)', default: lit(28) },
    },
  },
  {
    name: 'HeroBackdrop',
    displayName: 'Hero backdrop (sonar)',
    group: 'Panels',
    scenes: ['pack'],
    knobs: {
      tint: {
        kind: 'color',
        label: 'Animation tint (overlay blend)',
        default: lit('rgba(0,0,0,0)'),
      },
    },
  },

  // --- Custom template components (not in DS but configurable) ------------
  {
    name: 'RipButton',
    displayName: 'Buy button (RIP disc)',
    group: 'Buttons',
    scenes: ['pack'],
    knobs: {
      discFill: { kind: 'color', label: 'Disc fill', default: role('primary') },
      discText: { kind: 'color', label: 'Disc label colour', default: lit('#FFFFFF') },
      outerCollar: { kind: 'color', label: 'Outer collar', default: lit('#FFFFFF') },
      pillText: { kind: 'color', label: 'Price text colour', default: lit('#FFFFFF') },
    },
  },

  // --- Misc ----------------------------------------------------------------
  {
    name: 'Header',
    displayName: 'Top header bar',
    group: 'Misc',
    scenes: ['pack'],
    knobs: {
      bg: { kind: 'color', label: 'Background', default: role('heroFrom') },
      text: { kind: 'color', label: 'Text', default: role('textOnBrand') },
    },
  },
  {
    name: 'CategoryPill',
    displayName: 'Category pill (Lolitas / Golf / …)',
    group: 'Misc',
    scenes: ['pack'],
    knobs: {
      activeFill: { kind: 'color', label: 'Active fill', default: role('primary') },
      activeText: { kind: 'color', label: 'Active text', default: role('textOnBrand') },
      idleFill: { kind: 'color', label: 'Idle fill', default: lit('rgba(255,255,255,0.16)') },
      idleText: { kind: 'color', label: 'Idle text', default: role('textOnBrand') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(60) },
    },
  },
  {
    name: 'CategoryDropdownPill',
    displayName: 'Category dropdown (mobile)',
    group: 'Misc',
    scenes: ['pack'],
    template: 'modern',
    knobs: {
      activeFill: { kind: 'color', label: 'Selected row fill', default: lit('rgba(255,255,255,0.15)') },
      activeText: { kind: 'color', label: 'Selected row text', default: lit('#FFFFFF') },
      idleText: { kind: 'color', label: 'Idle / trigger text', default: lit('#FFFFFF') },
      tickColor: { kind: 'color', label: 'Selected tick colour', default: lit('#FFFFFF') },
    },
  },

  // --- New Plan-10 entries (Constructor partner roles) --------------------
  {
    name: 'PageBackground',
    displayName: 'Page background — global',
    group: 'Layout',
    // No `scenes` field = cross-cutting; shows under every scene group
    // (a partner-wide default for any page that doesn't override).
    knobs: {
      gradientFrom: { kind: 'color', label: 'Top / outer colour', default: role('pageBgFrom') },
      gradientTo: { kind: 'color', label: 'Bottom / inner colour', default: role('pageBgTo') },
    },
  },
  {
    name: 'PackPageBackground',
    displayName: 'Page background — Pack page',
    group: 'Layout',
    scenes: ['pack'],
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top / outer colour (overrides global)',
        default: inherit('PageBackground', 'gradientFrom'),
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom / inner colour (overrides global)',
        default: inherit('PageBackground', 'gradientTo'),
      },
    },
  },
  {
    name: 'JustPulledCard',
    displayName: 'Just Pulled card',
    group: 'Panels',
    scenes: ['just-pulled'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      // ── Title (e.g. "2019 Green Swirl Cards Kendrick Nunn ...") ───────
      titleColor: { kind: 'color', label: 'Title — colour', default: lit('#FFFFFF') },
      titleFont: {
        kind: 'fontFamily',
        label: 'Title — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(15) },

      // ── Offer name (e.g. "NEW WEB3") ──────────────────────────────────
      offerNameColor: { kind: 'color', label: 'Offer name — colour', default: lit('#FFFFFF') },
      offerNameFont: {
        kind: 'fontFamily',
        label: 'Offer name — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      offerNameSize: { kind: 'fontSize', label: 'Offer name — size (px)', default: lit(13) },

      // ── Time ago (e.g. "1 mo ago") ────────────────────────────────────
      timeAgoColor: { kind: 'color', label: 'Time ago — colour', default: lit('rgba(255,255,255,0.6)') },
      timeAgoFont: {
        kind: 'fontFamily',
        label: 'Time ago — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      timeAgoSize: { kind: 'fontSize', label: 'Time ago — size (px)', default: lit(13) },

      // ── Background ────────────────────────────────────────────────────
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'JustPulledHeader',
    displayName: 'Just Pulled — header',
    group: 'Panels',
    scenes: ['just-pulled'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      // ── Title "Just Pulled" ───────────────────────────────────────────
      titleColor: { kind: 'color', label: 'Title — colour', default: lit('#FFFFFF') },
      titleFont: {
        kind: 'fontFamily',
        label: 'Title — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(18) },

      // ── Back button ──────────────────────────────────────────────────
      backButtonBg: {
        kind: 'color',
        label: 'Back button — fill',
        default: lit('rgba(255,255,255,0.20)'),
      },
      backButtonBorder: {
        kind: 'color',
        label: 'Back button — border',
        default: lit('rgba(171,174,179,0.24)'),
      },
      backButtonBorderWidth: {
        kind: 'radius',
        label: 'Back button — border width (px)',
        default: lit(1),
      },
      backIconColor: { kind: 'color', label: 'Back icon colour', default: lit('#FFFFFF') },

      // ── Header background ────────────────────────────────────────────
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(0,0,0,0)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(0,0,0,0)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = page behind visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(0),
      },
    },
  },
  {
    name: 'JustPulledPageBackground',
    displayName: 'Page background — Just Pulled page',
    group: 'Layout',
    scenes: ['just-pulled'],
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top / outer colour (overrides global)',
        default: inherit('PageBackground', 'gradientFrom'),
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom / inner colour (overrides global)',
        default: inherit('PageBackground', 'gradientTo'),
      },
    },
  },
  {
    name: 'RevealPageBackground',
    displayName: 'Page background — Reveal page',
    group: 'Layout',
    scenes: ['reveal'],
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top colour (overrides global)',
        default: inherit('PageBackground', 'gradientFrom'),
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom colour (overrides global)',
        default: inherit('PageBackground', 'gradientTo'),
      },
    },
  },
  {
    name: 'MyPacksPageBackground',
    displayName: 'Page background — My Packs page',
    group: 'Layout',
    scenes: ['my-packs'],
    // My Packs intentionally does NOT inherit from the global PageBackground
    // / partner palette — the Figma design specifies a neutral dark grey
    // surface that should stay neutral regardless of the operator's primary
    // brand colour. Defaults are the literal Figma values; operators retint
    // by setting these knobs directly.
    knobs: {
      gradientFrom: {
        kind: 'color',
        label: 'Top colour',
        default: lit('#181818'),
      },
      gradientTo: {
        kind: 'color',
        label: 'Bottom colour',
        default: lit('#131313'),
      },
      bgImage: {
        // Optional photo / illustration layer painted on top of the
        // grey gradient (`cover` + centred). Operator pastes a raw URL;
        // the `imageUrl` kind auto-wraps it with `url("…")`. `none`
        // keeps the page flat-grey.
        kind: 'imageUrl',
        label: 'Background image (paste URL or none)',
        default: lit('none'),
      },
      noiseOpacity: {
        // Visibility of the noise-SVG overlay painted on top of the
        // gradient. `1` = the default Figma noise, `0` = clean flat
        // gradient. Drives the `--co-MyPacksPageBackground-noiseOpacity`
        // var on `[data-page='my-packs']::before`. The
        // `hideBackgroundPattern` prop on MyPacksPage / DeliveryPageShell
        // inlines the same variable as `0` for screen-local overrides.
        kind: 'opacity',
        label: 'Noise overlay opacity (0 = off)',
        default: lit(1),
      },
    },
  },
  {
    name: 'MyPacksHeader',
    displayName: 'My Packs — header bar',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
    },
  },
  {
    // Cross-cutting tab pill group. Rendered by the shared `<Tabs>`
    // design-system component so any tablist in the app picks up the
    // same theme tokens. Inspect via `data-co-component="Tabs"` on the
    // tablist `<div>`. All per-tab surface AND text knobs live here so
    // a single Design Constructor card themes every tablist at once.
    name: 'Tabs',
    displayName: 'Tab pill group (shared)',
    group: 'Panels',
    variants: ['to bottom', 'to right'],
    variantKnob: 'tabGroupBgDirection',
    variantLabel: 'Tab group background direction',
    knobs: {
      // Outer track — 5-knob gradient + border, same shape as
      // MyPacksItemCard so the operator's mental model stays consistent
      // across surfaces.
      tabGroupBgFrom: {
        kind: 'color',
        label: 'Tab group — background start',
        default: lit('rgba(255,255,255,0.20)'),
      },
      tabGroupBgTo: {
        kind: 'color',
        label: 'Tab group — background end',
        default: lit('rgba(153,153,153,0.20)'),
      },
      tabGroupBgDirection: {
        kind: 'text',
        label: 'Tab group — direction (use radio above)',
        default: lit('to bottom'),
      },
      tabGroupBgOpacity: {
        kind: 'opacity',
        label: 'Tab group — background opacity',
        default: lit(1),
      },
      tabGroupDarkTint: {
        kind: 'color',
        label: 'Tab group — dark tint base',
        default: lit('rgba(0,0,0,0.18)'),
      },
      tabGroupBorder: {
        kind: 'color',
        label: 'Tab group — border',
        default: lit('rgba(171,174,179,0.24)'),
      },
      // Active tab (inner pill) — same 5-knob gradient shape as the
      // outer track. `tabActiveBgDirection` is plain text (no radio)
      // because the schema supports a single `variantKnob` per
      // component and the outer track owns it.
      tabActiveBgFrom: {
        kind: 'color',
        label: 'Active tab — background start',
        default: lit('rgba(255,255,255,0.20)'),
      },
      tabActiveBgTo: {
        kind: 'color',
        label: 'Active tab — background end',
        default: lit('rgba(153,153,153,0.20)'),
      },
      tabActiveBgDirection: {
        kind: 'text',
        label: 'Active tab — direction (e.g. "to bottom")',
        default: lit('to bottom'),
      },
      tabActiveBgOpacity: {
        kind: 'opacity',
        label: 'Active tab — background opacity',
        default: lit(1),
      },
      tabActiveDarkTint: {
        kind: 'color',
        label: 'Active tab — dark tint base',
        default: lit('rgba(0,0,0,0.18)'),
      },
      tabActiveText: {
        kind: 'color',
        label: 'Active tab — text',
        default: lit('#FFFFFF'),
      },
      // Idle-tab text + tab font size. Live on `Tabs` (not on the
      // owning header) so a single sidebar card themes every tablist
      // in the app at once.
      tabIdleText: {
        kind: 'color',
        label: 'Idle tab — text',
        default: lit('#FFFFFF'),
      },
      tabIdleOpacity: {
        kind: 'opacity',
        label: 'Idle tab — opacity (0…1)',
        default: lit(0.5),
      },
      tabSize: { kind: 'fontSize', label: 'Tab — text size (px)', default: lit(15) },
    },
  },
  {
    name: 'MyPacksStatsRow',
    displayName: 'My Packs — stats row',
    group: 'Panels',
    scenes: ['my-packs'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      valueColor: { kind: 'color', label: 'Value colour', default: lit('#FFFFFF') },
      valueSize: { kind: 'fontSize', label: 'Value size (px)', default: lit(15) },
      separatorColor: {
        kind: 'color',
        label: 'Separator dot colour',
        default: lit('rgba(255,255,255,0.40)'),
      },
    },
  },
  {
    name: 'MyPacksUnopenedPackCard',
    displayName: 'My Packs — unopened pack tile',
    group: 'Panels',
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      bgImage: {
        // `imageUrl` kind auto-wraps a raw URL with `url("…")` at apply
        // time, so the operator can paste a plain URL directly from the
        // browser address bar. Default `none` keeps the tile flat-grey;
        // any URL or `linear-gradient(...)` overlays on top of the
        // inherited MyPacksItemCard glass background.
        kind: 'imageUrl',
        label: 'Background image (paste URL or none)',
        default: lit('none'),
      },
      // Background gradient — own knobs so the Packs tab can be themed
      // independently of Cards. The CSS-level fallback chain in
      // UnopenedPackCard reads MyPacksItemCard's value when a slot here
      // is unset, so leaving everything empty keeps both tabs in sync;
      // setting any of these peels Packs off into its own look.
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
      },
      borderColor: {
        kind: 'color',
        label: 'Border colour',
        default: lit('rgba(171,174,179,0.24)'),
      },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px)',
        default: lit(2),
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      subtitleColor: { kind: 'color', label: 'Subtitle colour', default: lit('#EAEAEB') },
    },
  },
  {
    name: 'MyPacksItemCard',
    displayName: 'My Packs — card item',
    group: 'Panels',
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(2),
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
      },
      photoBg: {
        kind: 'color',
        label: 'Photo container — background',
        default: lit('rgba(255,255,255,0.03)'),
      },
      timerBg: {
        kind: 'color',
        label: 'Timer chip — fill',
        default: lit('rgba(255,255,255,0.15)'),
      },
      timerText: {
        kind: 'color',
        label: 'Timer chip — text',
        default: lit('#FFFFFF'),
      },
      timerProgressTrack: {
        kind: 'color',
        label: 'Timer pie — elapsed track',
        default: lit('rgba(255,255,255,0.10)'),
      },
      timerProgressUrgent: {
        kind: 'color',
        label: 'Timer pie — remaining (urgent)',
        default: lit('#DBB254'),
      },
      timerProgressTrackUrgent: {
        kind: 'color',
        label: 'Timer pie — elapsed track (urgent)',
        default: lit('rgba(255,180,95,0.10)'),
      },
      buybackText: {
        kind: 'color',
        label: 'Buyback button — text',
        default: lit('#FFFFFF'),
      },
      // Selected-state visuals — used when the card is part of the My
      // Packs bulk-selection set (driven by `data-selected="true"`).
      // Defaults match the Buyback green so the highlight reads as
      // "this is what Buyback will sell"; operator can retint freely.
      selectedBorderColor: {
        kind: 'color',
        label: 'Selected — border colour',
        default: lit('#23D11D'),
      },
      checkmarkBg: {
        kind: 'color',
        label: 'Selected — checkmark background',
        default: lit('#23D11D'),
      },
      checkmarkColor: {
        kind: 'color',
        label: 'Selected — checkmark colour',
        default: lit('#FFFFFF'),
      },
    },
  },
  {
    // Card surface used on the Deliveries tab — one tile per pool_shipping.
    // Mirrors the MyPacksItemCard knob set so partners get the same
    // expressiveness for the deliveries list as they have for the cards
    // list. Inspect via `data-co-component="MyPacksDeliveryCard"` on the
    // tile itself.
    name: 'MyPacksDeliveryCard',
    displayName: 'My Packs — delivery tile',
    group: 'Panels',
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: { kind: 'radius', label: 'Glass blur (px, 0 = no blur)', default: lit(27) },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
      },
    },
  },
  {
    // Outer card surface used on Delivery New / Delivery Show pages —
    // the big rounded glass panel that wraps the form / detail content.
    // Same knob shape as MyPacksItemCard so partners can theme the
    // Delivery flow independently. Inspect via
    // `data-co-component="DeliveryFormCard"` on the wrapper.
    name: 'DeliveryFormCard',
    displayName: 'Delivery — form card',
    group: 'Panels',
    scenes: ['my-packs'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: { kind: 'radius', label: 'Glass blur (px, 0 = no blur)', default: lit(27) },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.20)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      darkTint: {
        kind: 'color',
        label: 'Dark tint base (under glass)',
        default: lit('rgba(0,0,0,0.18)'),
      },
    },
  },
  {
    name: 'MyPacksToolbarSubmit',
    displayName: 'My Packs — Submit button',
    group: 'Buttons',
    scenes: ['my-packs'],
    // Literal Figma green — see MyPacksPageBackground for rationale on
    // skipping operator-palette inheritance on this page.
    knobs: {
      fill: { kind: 'color', label: 'Fill', default: lit('#23D11D') },
    },
  },
  {
    name: 'MyPacksFooter',
    displayName: 'My Packs — footer links',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      linkColor: {
        kind: 'color',
        label: 'Link colour',
        default: lit('#DCDCDC'),
      },
      linkSize: { kind: 'fontSize', label: 'Link size (px)', default: lit(15) },
    },
  },
  {
    name: 'MyPacksEmptyState',
    displayName: 'My Packs — empty state',
    group: 'Panels',
    scenes: ['my-packs'],
    knobs: {
      iconBgFrom: {
        kind: 'color',
        label: 'Icon disc — top colour',
        default: lit('rgba(255,255,255,0.10)'),
      },
      iconBgTo: {
        kind: 'color',
        label: 'Icon disc — bottom colour',
        default: lit('rgba(255,255,255,0.04)'),
      },
      iconBorder: {
        kind: 'color',
        label: 'Icon disc — border',
        default: lit('rgba(255,255,255,0.10)'),
      },
      iconColor: {
        kind: 'color',
        label: 'Icon colour',
        default: lit('rgba(255,255,255,0.55)'),
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title size (px)', default: lit(18) },
      descriptionColor: {
        kind: 'color',
        label: 'Description colour',
        default: lit('rgba(255,255,255,0.60)'),
      },
    },
  },
  {
    // Shared modal frame — backdrop tint + blur. Cross-cutting (no
    // `scenes`) so a single change applies to every modal in the app.
    // Per-modal content (titles, cards, buttons) keeps its OWN
    // component entry below — those knobs theme only that modal's
    // body, not the wrapper.
    name: 'Modal',
    displayName: 'Modal (shared frame)',
    group: 'Modals',
    knobs: {
      backdropFill: {
        kind: 'color',
        label: 'Backdrop tint',
        default: lit('rgba(0,0,0,0.45)'),
      },
      backdropBlur: {
        kind: 'radius',
        label: 'Backdrop blur (px)',
        default: lit(20),
      },
    },
  },
  {
    name: 'BuybackConfirmModal',
    displayName: 'Buyback confirmation modal',
    group: 'Modals',
    scenes: ['reveal'],
    knobs: {
      // FMV card — the white pill with "Fair Market Value" / "Buyback
      // Offer" inside the modal. Border colour is the green ring that
      // frames it; fill is the card background.
      cardFill: {
        kind: 'color',
        label: 'FMV card — fill',
        default: lit('#FFFFFF'),
      },
      cardBorder: {
        kind: 'color',
        label: 'FMV card — border',
        default: role('primary'),
      },
      fmvRowBg: {
        kind: 'color',
        label: 'FMV row — background tint',
        default: lit('rgba(34,225,28,0.06)'),
      },
      fmvLabelColor: {
        kind: 'color',
        label: 'FMV — label colour',
        default: lit('rgba(0,0,0,0.55)'),
      },
      fmvValueColor: {
        kind: 'color',
        label: 'FMV — value colour',
        default: lit('rgba(0,0,0,0.70)'),
      },
      offerLabelColor: {
        kind: 'color',
        label: 'Buyback Offer — label colour',
        default: lit('rgba(0,0,0,0.55)'),
      },
      offerValueColor: {
        kind: 'color',
        label: 'Buyback Offer — value colour',
        default: lit('#15181E'),
      },
      checkboxTextColor: {
        kind: 'color',
        label: '"Don’t show again" label colour',
        default: lit('#15181E'),
      },
      checkColor: { kind: 'color', label: 'Checkmark colour', default: lit('#FFFFFF') },
      checkBoxFill: { kind: 'color', label: 'Checkbox fill (checked)', default: lit('#22E11C') },
      checkBoxBorder: { kind: 'color', label: 'Checkbox border (checked)', default: lit('#22E11C') },
      // Both modal CTAs (`Accept Offer & Rip Again` and `Accept Offer`)
      // are the DS `Button` in `primary` / `secondary` variants — no
      // modal-specific button knobs here. Operators theme them via
      // the global `Button → primary` / `Button → secondary` knobs
      // and the change applies everywhere consistently.
    },
  },

  // ─── Shipping modal ─────────────────────────────────────────
  // ONE registry entry for the single reusable ShippingModal (Figma
  // 703:28734), shown in BOTH the reveal "Create Shipping" flow and
  // the My Packs ship disclaimer. Every section (countdown, title /
  // description, fees breakdown, note) is themed under the one
  // `--co-ShippingModal-*` namespace — right-click anywhere on the
  // sheet opens this single DC slot.
  //
  // Backdrop is NOT a per-modal knob — the dialog reads
  // `--co-Modal-backdropFill` straight from the shared DS Modal.
  //
  // Buttons (Buyback / Confirm Shipping / Add Delivery) stay on the
  // global DS Button knobs (`primary` + `glass-frosted`) so they theme
  // alongside every other CTA in the app — no per-modal button knobs.
  {
    name: 'ShippingModal',
    displayName: 'Shipping modal',
    group: 'Modals',
    scenes: ['reveal', 'my-packs'],
    knobs: {
      cardFill: {
        kind: 'color',
        label: 'Sheet — fill',
        default: lit('#FFFFFF'),
      },
      // Countdown header (reveal flow)
      countdownLabelFont: {
        kind: 'fontFamily',
        label: 'Countdown — caption font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      countdownLabelSize: {
        kind: 'fontSize',
        label: 'Countdown — caption size (px)',
        default: lit(15),
      },
      countdownLabelColor: {
        kind: 'color',
        label: 'Countdown — caption colour',
        default: lit('rgba(0,0,0,0.85)'),
      },
      chipFont: {
        kind: 'fontFamily',
        label: 'Countdown chip — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      chipSize: {
        kind: 'fontSize',
        label: 'Countdown chip — size (px)',
        default: lit(15),
      },
      chipFill: {
        kind: 'color',
        label: 'Countdown chip — fill',
        default: lit('rgba(0,0,0,0.07)'),
      },
      chipText: {
        kind: 'color',
        label: 'Countdown chip — text',
        default: lit('rgba(0,0,0,0.85)'),
      },
      // Title + lead description (disclaimer flow)
      titleColor: {
        kind: 'color',
        label: 'Title — colour',
        default: lit('#15181E'),
      },
      titleSize: {
        kind: 'fontSize',
        label: 'Title — size (px)',
        default: lit(17),
      },
      descFont: {
        kind: 'fontFamily',
        label: 'Description — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      descSize: {
        kind: 'fontSize',
        label: 'Description — size (px)',
        default: lit(16),
      },
      descColor: {
        kind: 'color',
        label: 'Description — colour',
        default: lit('rgba(0,0,0,0.75)'),
      },
      // Fees breakdown (positions)
      feeCardFill: {
        kind: 'color',
        label: 'Fees card — fill',
        default: lit('#FFFFFF'),
      },
      feeCardBorder: {
        kind: 'color',
        label: 'Fees card — border',
        default: lit('rgba(0,0,0,0.08)'),
      },
      rowFont: {
        kind: 'fontFamily',
        label: 'Fee row — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      rowLabelSize: {
        kind: 'fontSize',
        label: 'Fee row — label size (px)',
        default: lit(17),
      },
      rowLabelColor: {
        kind: 'color',
        label: 'Fee row — label colour',
        default: lit('rgba(0,0,0,0.55)'),
      },
      rowValueSize: {
        kind: 'fontSize',
        label: 'Fee row — value size (px)',
        default: lit(17),
      },
      rowValueColor: {
        kind: 'color',
        label: 'Fee row — value colour',
        default: lit('#15181E'),
      },
      rowDivider: {
        kind: 'color',
        label: 'Fee row — divider colour',
        default: lit('rgba(0,0,0,0.06)'),
      },
      // Note / fine print (below the breakdown)
      noteFont: {
        kind: 'fontFamily',
        label: 'Note — font',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      noteSize: {
        kind: 'fontSize',
        label: 'Note — size (px)',
        default: lit(16),
      },
      noteColor: {
        kind: 'color',
        label: 'Note — colour',
        default: lit('rgba(0,0,0,0.75)'),
      },
      // Close (X) chip
      closeFill: {
        kind: 'color',
        label: 'Close (X) — fill',
        default: lit('rgba(0,0,0,0.6)'),
      },
      closeIconColor: {
        kind: 'color',
        label: 'Close (X) — icon colour',
        default: lit('#FFFFFF'),
      },
    },
  },
  {
    name: 'AddressModal',
    displayName: 'Address form — modal',
    group: 'Modals',
    scenes: ['my-packs'],
    knobs: {
      cardFill: { kind: 'color', label: 'Card — fill', default: lit('#FFFFFF') },
      textColor: { kind: 'color', label: 'Title / text — colour', default: lit('#15181E') },
      linkColor: {
        kind: 'color',
        label: '"Enter manually" link — colour',
        default: lit('rgba(21,24,30,0.6)'),
      },
      closeFill: {
        kind: 'color',
        label: 'Close (X) — fill',
        default: lit('rgba(0,0,0,0.05)'),
      },
      closeIconColor: {
        kind: 'color',
        label: 'Close (X) — icon colour',
        default: lit('#15181E'),
      },
    },
  },
  {
    name: 'AddressPickerModal',
    displayName: 'Address picker — modal',
    group: 'Modals',
    scenes: ['my-packs'],
    knobs: {
      cardFill: { kind: 'color', label: 'Card — fill', default: lit('#FFFFFF') },
      textColor: { kind: 'color', label: 'Title / text — colour', default: lit('#15181E') },
      closeFill: { kind: 'color', label: 'Close (X) — fill', default: lit('rgba(0,0,0,0.05)') },
      closeIconColor: { kind: 'color', label: 'Close (X) — icon colour', default: lit('#15181E') },
      rowText: { kind: 'color', label: 'Address row — text colour', default: lit('#15181E') },
      rowFill: { kind: 'color', label: 'Address row — fill', default: lit('rgba(0,0,0,0.02)') },
      rowBorder: { kind: 'color', label: 'Address row — border', default: lit('rgba(0,0,0,0.10)') },
      rowSelectedFill: {
        kind: 'color',
        label: 'Address row (selected) — fill',
        default: lit('rgba(34,225,28,0.07)'),
      },
      rowSelectedBorder: {
        kind: 'color',
        label: 'Address row (selected) — border / check',
        default: lit('#16B814'),
      },
      radioBorder: {
        kind: 'color',
        label: 'Radio (unselected) — border',
        default: lit('rgba(0,0,0,0.25)'),
      },
    },
  },
  {
    name: 'TextField',
    displayName: 'Text input (DS)',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      fill: { kind: 'color', label: 'Input — fill', default: lit('#F2F2F2') },
      border: { kind: 'color', label: 'Input — border', default: lit('#E2E2E5') },
      textColor: { kind: 'color', label: 'Input — text colour', default: lit('#15181E') },
      labelColor: { kind: 'color', label: 'Label — colour', default: lit('#6B7280') },
      focusRing: {
        kind: 'color',
        label: 'Focus ring — colour',
        default: lit('rgba(34,225,28,0.35)'),
      },
    },
  },
  {
    name: 'DefaultBadge',
    displayName: 'Default-address badge',
    group: 'Misc',
    scenes: ['my-packs'],
    knobs: {
      bg: { kind: 'color', label: 'Badge — fill', default: lit('rgba(34,225,28,0.16)') },
      fg: { kind: 'color', label: 'Badge — text colour', default: lit('#16B814') },
    },
  },

  // ─── Winner screen elements ─────────────────────────────────
  {
    name: 'BadgeRevealSequence',
    displayName: 'Badge reveal — character/grade text',
    group: 'Reveal',
    scenes: ['reveal'],
    knobs: {
      color: {
        kind: 'color',
        label: 'Text colour',
        default: lit('#15181E'),
      },
    },
  },
  {
    name: 'WinnerStepTitle',
    displayName: 'Winner screen — title',
    group: 'Reveal',
    scenes: ['reveal'],
    knobs: {
      font: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      size: {
        kind: 'fontSize',
        label: 'Font size (px)',
        default: lit(32),
      },
      color: {
        kind: 'color',
        label: 'Text colour',
        default: lit('#FFFFFF'),
      },
    },
  },
  {
    name: 'WinnerStepFMV',
    displayName: 'Winner screen — Fair Market Value line',
    group: 'Reveal',
    scenes: ['reveal'],
    knobs: {
      font: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      size: {
        kind: 'fontSize',
        label: 'Font size (px)',
        default: lit(14),
      },
      color: {
        kind: 'color',
        label: 'Text colour',
        default: lit('rgba(255,255,255,0.60)'),
      },
    },
  },
  {
    name: 'WinnerStepProfit',
    displayName: 'Winner screen — PROFIT line',
    group: 'Reveal',
    scenes: ['reveal'],
    knobs: {
      font: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      size: {
        kind: 'fontSize',
        label: 'Font size (px)',
        default: lit(44),
      },
      labelColor: {
        kind: 'color',
        label: 'Label "PROFIT" colour',
        default: lit('#FFFFFF'),
      },
      valueColor: {
        kind: 'color',
        label: 'Amount "+$X.XX" colour',
        default: lit('#23D11D'),
      },
    },
  },
  // "Create Shipping" footer link uses the DS `Button → ghost`
  // variant — no dedicated `WinnerStepShipping` component here.
  // Theming flows through the shared `Button → ghost` knobs.
  {
    name: 'PotentialHits',
    displayName: 'Potential Hits panel',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      labelColor: { kind: 'color', label: 'Label colour ("POTENTIAL HITS")', default: lit('#FFFFFF') },
      labelSize: { kind: 'fontSize', label: 'Label size (px)', default: lit(18) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'JustPulled',
    displayName: 'Just Pulled feed',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      labelColor: { kind: 'color', label: 'Label colour ("JUST PULLED")', default: lit('#FFFFFF') },
      labelSize: { kind: 'fontSize', label: 'Label size (px)', default: lit(18) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      chevronBg: { kind: 'color', label: 'Chevron background', default: lit('rgba(255,255,255,0.20)') },
      chevronIcon: { kind: 'color', label: 'Chevron icon colour', default: lit('#FFFFFF') },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'OddsCard',
    displayName: 'Odds card (classic)',
    group: 'Panels',
    scenes: ['pack'],
    template: 'classic',
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      // ── ODDS heading ─────────────────────────────────────────────
      titleColor: { kind: 'color', label: 'Title — colour ("ODDS")', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title — size (px)', default: lit(18) },
      // ── Section labels (Big win chance, Expected Value, Common, Uncommon, % Chance) ─
      labelColor: { kind: 'color', label: 'Label — colour', default: lit('rgba(255,255,255,0.5)') },
      labelSize: { kind: 'fontSize', label: 'Label — size (px)', default: lit(13) },
      // ── Stat values (33.34%, $5 - $15) ──────────────────────────
      valueColor: { kind: 'color', label: 'Value — colour', default: lit('#FFFFFF') },
      valueSize: { kind: 'fontSize', label: 'Value — size (px)', default: lit(18) },
      // ── Background ──────────────────────────────────────────────
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(2),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'StatsRow',
    displayName: 'Pack stats row',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      valueColor: { kind: 'color', label: 'Value colour ($, %, …)', default: lit('#FFFFFF') },
      valueSize: { kind: 'fontSize', label: 'Value size (px)', default: lit(20) },
      labelColor: {
        kind: 'color',
        label: 'Label colour (Min Value / Big Win Chance)',
        default: lit('rgba(255,255,255,0.5)'),
      },
      labelSize: { kind: 'fontSize', label: 'Label size (px)', default: lit(13) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(0),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px, desktop ≥900px)', default: lit(28) },
    },
  },
  {
    name: 'SliderDots',
    displayName: 'Carousel dots',
    group: 'Misc',
    scenes: ['pack'],
    knobs: {
      active: { kind: 'color', label: 'Active dot', default: lit('rgba(255,255,255,1)') },
      idle: { kind: 'color', label: 'Idle dot', default: lit('rgba(255,255,255,0.4)') },
    },
  },
  {
    // Mobile pack title — the text shown under the pedestal when the pack has
    // no logo image. Drives `--co-PackTitle-*`, consumed by the modern Slider.
    name: 'PackTitle',
    displayName: 'Pack title (mobile)',
    group: 'Misc',
    scenes: ['pack'],
    template: 'modern',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      size: { kind: 'fontSize', label: 'Font size (px)', default: lit(24) },
      weight: { kind: 'fontWeight', label: 'Font weight', default: lit(700) },
      color: { kind: 'color', label: 'Text colour', default: lit('#FFFFFF') },
    },
  },
  {
    name: 'BottomActionShell',
    displayName: 'Buy button — shell',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'gradientDirection',
    variantLabel: 'Gradient direction',
    knobs: {
      gradientFrom: { kind: 'color', label: 'Shell colour — start', default: lit('#FFFFFF') },
      gradientTo: { kind: 'color', label: 'Shell colour — end', default: lit('#FFFFFF') },
      gradientDirection: {
        kind: 'text',
        label: 'Gradient direction (use radio above)',
        default: lit('to bottom'),
      },
      border: { kind: 'color', label: 'Shell border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: { kind: 'radius', label: 'Shell border width (px, 0 = hidden)', default: lit(2) },
    },
  },
  {
    name: 'CardModal',
    displayName: 'Card modal (preview)',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title size (px)', default: lit(26) },
      labelColor: { kind: 'color', label: 'Label chip text', default: lit('#FFFFFF') },
      labelSize: { kind: 'fontSize', label: 'Label chip size (px)', default: lit(14) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = backdrop visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(1),
      },
      backdropTint: {
        kind: 'color',
        label: 'Backdrop tint (page behind modal)',
        default: lit('rgba(0,0,0,0.60)'),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'PackInfoCard',
    displayName: 'Pack info card (sidebar)',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'bgDirection',
    variantLabel: 'Background direction',
    knobs: {
      fontFamily: {
        kind: 'fontFamily',
        label: 'Font family (title + subtitle)',
        default: lit('Inter'),
        options: FONT_FAMILIES,
      },
      titleColor: { kind: 'color', label: 'Title colour', default: lit('#FFFFFF') },
      titleSize: { kind: 'fontSize', label: 'Title size (px)', default: lit(24) },
      subtitleColor: { kind: 'color', label: 'Subtitle colour', default: lit('rgba(255,255,255,0.8)') },
      subtitleSize: { kind: 'fontSize', label: 'Subtitle size (px)', default: lit(13) },
      bgFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(255,255,255,0.20)') },
      bgTo: { kind: 'color', label: 'Background — end', default: lit('rgba(153,153,153,0.20)') },
      bgDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      bgOpacity: {
        kind: 'opacity',
        label: 'Background opacity (lower = blur visible)',
        default: lit(1),
      },
      glassBlur: {
        kind: 'radius',
        label: 'Glass blur (px, 0 = no blur)',
        default: lit(27),
      },
      borderColor: { kind: 'color', label: 'Border colour', default: lit('rgba(171,174,179,0.24)') },
      borderWidth: {
        kind: 'radius',
        label: 'Border width (px, 0 = hidden)',
        default: lit(2),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(28) },
    },
  },
  {
    name: 'TierCard',
    displayName: 'Tier card (Pro / Premier / …)',
    group: 'Panels',
    scenes: ['pack'],
    variants: ['to bottom', 'to right'],
    variantKnob: 'gradientDirection',
    variantLabel: 'Background gradient direction',
    knobs: {
      titleText: { kind: 'color', label: 'Tier name colour', default: lit('#FFFFFF') },
      priceText: { kind: 'color', label: 'Price colour', default: lit('#FFFFFF') },
      gradientFrom: { kind: 'color', label: 'Background — start', default: lit('rgba(191,191,191,0.13)') },
      gradientTo: { kind: 'color', label: 'Background — end', default: lit('rgba(0,0,0,0.06)') },
      gradientDirection: {
        kind: 'text',
        label: 'Direction (use radio above)',
        default: lit('to bottom'),
      },
      activeBorder: { kind: 'color', label: 'Active border colour', default: lit('#23D11D') },
      activeBorderWidth: {
        kind: 'radius',
        label: 'Active border width (px, 0 = hidden)',
        default: lit(2),
      },
      borderRadius: { kind: 'radius', label: 'Border radius (px)', default: lit(16) },
    },
  },
  {
    name: 'QuantityStepper',
    displayName: 'Quantity stepper',
    group: 'Buttons',
    scenes: ['pack'],
    knobs: {
      containerFill: { kind: 'color', label: 'Pod outer fill', default: lit('#FFFFFF') },
      backgroundFrom: { kind: 'color', label: 'Inner background — start', default: lit('#E0E0E0') },
      backgroundTo: { kind: 'color', label: 'Inner background — end', default: lit('#FFFFFF') },
      buttonFill: { kind: 'color', label: '± button fill', default: lit('#FFFFFF') },
      buttonIcon: { kind: 'color', label: '± icon colour', default: lit('#15181E') },
      valueText: { kind: 'color', label: 'Number colour', default: lit('#15181E') },
    },
  },
];

export function findComponent(name: string): ComponentSchema | undefined {
  return COMPONENTS.find((c) => c.name === name);
}

export function findKnob(
  componentName: string,
  knobName: string,
): KnobDef | undefined {
  return findComponent(componentName)?.knobs[knobName];
}
